import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { namespacedKey } from '@/components/hooks/useLocalStorage';

import type { ColumnDef, ColumnLayout } from './types';

const MIN_WIDTH = 36;

/**
 * Reads a persisted layout, tolerating the legacy width-only shape that
 * `useResizableColumns` wrote (`{ straw: 60, cane: 80 }`) before column
 * ordering existed. Legacy blobs are lifted into the new shape so users keep
 * the widths they had already dragged.
 */
function loadStored(storageKey: string): Partial<ColumnLayout> | null {
  try {
    const raw = localStorage.getItem(namespacedKey(storageKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;

    const maybe = parsed as Partial<ColumnLayout> & Record<string, unknown>;
    if (maybe.widths && typeof maybe.widths === 'object') {
      return {
        widths: maybe.widths,
        order: Array.isArray(maybe.order) ? maybe.order : undefined,
        hidden: Array.isArray(maybe.hidden) ? maybe.hidden : undefined,
      };
    }

    const legacy = Object.entries(maybe).filter(
      ([, v]) => typeof v === 'number',
    );
    if (!legacy.length) return null;
    return { widths: Object.fromEntries(legacy) as Record<string, number> };
  } catch {
    return null;
  }
}

/**
 * Merges the persisted order with the column definitions. Columns the user has
 * never seen (newly added in code) are appended rather than dropped, and keys
 * left over from removed columns are ignored, so a deploy that changes the
 * column set never strands a user with a broken saved layout.
 */
function resolveOrder<Row>(
  columns: ColumnDef<Row>[],
  stored: string[] | undefined,
): ColumnDef<Row>[] {
  if (!stored?.length) return columns;
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const ordered: ColumnDef<Row>[] = [];
  for (const key of stored) {
    const col = byKey.get(key);
    if (col) {
      ordered.push(col);
      byKey.delete(key);
    }
  }
  for (const col of columns) {
    if (byKey.has(col.key)) ordered.push(col);
  }
  return ordered;
}

export function useColumnLayout<Row>(
  storageKey: string,
  columns: ColumnDef<Row>[],
) {
  // Read once during the initial render so a saved layout is applied on the
  // very first paint, matching WithLocalStorage's load-on-mount behaviour.
  const [layout, setLayout] = useState<ColumnLayout>(() => {
    const stored = loadStored(storageKey);
    return {
      widths: {
        ...Object.fromEntries(columns.map((c) => [c.key, c.width])),
        ...stored?.widths,
      },
      order: stored?.order ?? columns.map((c) => c.key),
      hidden:
        stored?.hidden ?? columns.filter((c) => c.hidden).map((c) => c.key),
    };
  });

  const orderedColumns = useMemo(
    () => resolveOrder(columns, layout.order),
    [columns, layout.order],
  );

  const hiddenSet = useMemo(() => new Set(layout.hidden), [layout.hidden]);

  /** The columns actually rendered: ordered, minus anything hidden. */
  const visibleColumns = useMemo(
    () => orderedColumns.filter((col) => !hiddenSet.has(col.key)),
    [orderedColumns, hiddenSet],
  );

  const totalWidth = useMemo(
    () =>
      visibleColumns.reduce(
        (sum, col) => sum + (layout.widths[col.key] ?? col.width),
        0,
      ),
    [visibleColumns, layout.widths],
  );

  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  useEffect(() => {
    try {
      localStorage.setItem(namespacedKey(storageKey), JSON.stringify(layout));
    } catch {
      // storage unavailable — layout still works for this session
    }
  }, [storageKey, layout]);

  const dragRef = useRef<{
    key: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = e.clientX - drag.startX;
    setLayout((prev) => ({
      ...prev,
      widths: {
        ...prev.widths,
        [drag.key]: Math.max(MIN_WIDTH, drag.startWidth + delta),
      },
    }));
  }, []);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const startResize = useCallback(
    (key: string, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = {
        key,
        startX: e.clientX,
        startWidth: layoutRef.current.widths[key] ?? MIN_WIDTH,
      };
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
    },
    [onPointerMove, onPointerUp],
  );

  /** Moves `sourceKey` to the position currently held by `targetKey`. */
  const moveColumn = useCallback(
    (sourceKey: string, targetKey: string) => {
      if (sourceKey === targetKey) return;
      setLayout((prev) => {
        const order = prev.order.length
          ? [...prev.order]
          : columns.map((c) => c.key);
        const from = order.indexOf(sourceKey);
        const to = order.indexOf(targetKey);
        if (from === -1 || to === -1) return prev;
        order.splice(to, 0, ...order.splice(from, 1));
        return { ...prev, order };
      });
    },
    [columns],
  );

  const toggleColumn = useCallback((key: string, visible: boolean) => {
    setLayout((prev) => {
      const hidden = prev.hidden.filter((k) => k !== key);
      return { ...prev, hidden: visible ? hidden : [...hidden, key] };
    });
  }, []);

  const resetLayout = useCallback(() => {
    setLayout({
      widths: Object.fromEntries(columns.map((c) => [c.key, c.width])),
      order: columns.map((c) => c.key),
      hidden: columns.filter((c) => c.hidden).map((c) => c.key),
    });
  }, [columns]);

  return {
    orderedColumns,
    visibleColumns,
    hiddenKeys: hiddenSet,
    widths: layout.widths,
    totalWidth,
    startResize,
    moveColumn,
    toggleColumn,
    resetLayout,
  };
}
